import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { translateOutsideReact } from '@/i18n/translateOutsideReact';
import { handleError } from '@/utils/handleError';
import { extractFunctionError, throwIfLegalFormChangeNeedsConfirmation } from '@/utils/errorUtils';

export interface CompanyContext {
  id: string;
  company_id: string;
  context_text: string;
  company_type: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export class CompanySettingsApiClient {
  /**
   * Get company context for the current user's active company
   * @returns Company context
   */
  static async getContext(): Promise<CompanyContext> {
    try {
      const { data, error } = await supabase.functions.invoke('company-settings-api', {
        body: { action: 'getContext' },
      });

      if (error) throw error;

      return data.context;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchCompanyContext'), 'getContext');
      throw error;
    }
  }

  /**
   * Update company context for the current user's active company.
   *
   * A legal-form change after booked tax rows is held by the backend until it is resent with
   * `confirmLegalFormChange` (FIN-1167 EC3). That 409 is thrown as a CodedError and neither
   * toasted nor reported: the settings form asks the user instead.
   */
  static async updateContext(
    contextText: string,
    companyType?: string | null,
    options: { confirmLegalFormChange?: boolean } = {},
  ): Promise<CompanyContext> {
    try {
      const { data, error } = await supabase.functions.invoke('company-settings-api', {
        body: {
          action: 'updateContext',
          contextText,
          companyType,
          confirmLegalFormChange: options.confirmLegalFormChange === true,
        },
      });

      if (error) throw error;

      toast.success(translateOutsideReact('consultantToasts.api.companyContextUpdated'));
      return data.context;
    } catch (error) {
      const body = await extractFunctionError(error);
      throwIfLegalFormChangeNeedsConfirmation(body);
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToUpdateCompanyContext'), 'updateContext', body);
      throw error;
    }
  }
}
